import { DisplayRightsComposer } from '@/components/display-rights/DisplayRightsComposer';
import type { DisplayRightsMetaState } from '@/components/display-rights/display-rights-status';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import type { MentionBoostAuthorizationScope } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

interface MentionBoostProposalStepProps {
  authorizationId: string;
  /** Creator handle, used to personalise the pre-filled message. */
  creatorHandle?: string;
  metaState?: DisplayRightsMetaState;
  /** What the just-requested authorization covers. */
  scope?: MentionBoostAuthorizationScope;
  onClose: () => void;
}

/**
 * The optional step after the Meta authorization has been requested: offer the
 * creator compensation for the right to run their content.
 *
 * Optional on purpose. The authorization request has already left, unchanged,
 * and nothing here can take it back — so this step is skippable, and a failure
 * to compose leaves the request exactly where it was. The same offer stays
 * reachable from the contact card afterwards.
 */
export function MentionBoostProposalStep({
  authorizationId,
  creatorHandle,
  metaState,
  scope,
  onClose,
}: MentionBoostProposalStepProps) {
  const { t } = useTranslation();

  return (
    <Flex flex="1" minH={0} direction="column" data-testid="mention-boost-proposal-step">
      <Box flex="1" overflowY="auto" px={{ base: 4, md: 6 }} pt={5} pb={6}>
        <VStack align="stretch" gap={4}>
          <Text fontSize="sm" color="gray.700" lineHeight="tall">
            {t('mentions.boost.proposal.description')}
          </Text>

          <DisplayRightsComposer
            authorizationId={authorizationId}
            creatorHandle={creatorHandle}
            metaState={metaState}
            scope={scope}
            secondaryAction={{ label: t('mentions.boost.proposal.skip'), onClick: onClose }}
            onDone={onClose}
          />
        </VStack>
      </Box>
    </Flex>
  );
}
